import { redactSensitive } from '../security/redactSensitive';

const API_URL = process.env.REACT_APP_API_URL;
const IS_PROD = process.env.NODE_ENV === 'production';

if (!IS_PROD && !API_URL) {
  console.warn(
    '%c[Config] REACT_APP_API_URL .env dosyasında tanımlanmamış. ' +
    'Tüm API çağrıları başarısız olacak — .env dosyasını kontrol edin.',
    'color:orange;font-weight:bold;'
  );
}

// ---------------------------------------------------------------------------
// 1) Duplicate request prevention – aynı process ID eşzamanlı çalışamaz
// ---------------------------------------------------------------------------
const activeRequests = new Map();

// ---------------------------------------------------------------------------
// 2) Concurrent request queue – max 5 paralel istek
// ---------------------------------------------------------------------------
const MAX_CONCURRENT = 5;
let runningCount = 0;
const waitQueue = [];

const acquireSlot = () =>
  new Promise((resolve) => {
    if (runningCount < MAX_CONCURRENT) {
      runningCount++;
      resolve();
    } else {
      waitQueue.push(resolve);
    }
  });

const releaseSlot = () => {
  runningCount--;
  if (waitQueue.length > 0) {
    runningCount++;
    const next = waitQueue.shift();
    next();
  }
};

// ---------------------------------------------------------------------------
// 3) Request timeout – 15 saniye
// ---------------------------------------------------------------------------
const REQUEST_TIMEOUT = 15_000;

// ---------------------------------------------------------------------------
// Caller detection (dev only)
// ---------------------------------------------------------------------------
const getCallingFunction = () => {
  try {
    const stack = new Error().stack;
    const lines = stack.split('\n');
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.includes('fetchWrapper') || line.includes('enhancedFetchWrapper')) continue;
      const match = line.match(/at\s+(\w+\.?\w*)/);
      if (match && match[1] && match[1] !== 'Error') return match[1];
    }
    return 'Unknown';
  } catch {
    return 'Error Detecting Function';
  }
};

// ---------------------------------------------------------------------------
// Main fetchWrapper
// ---------------------------------------------------------------------------
export const fetchWrapper = async (process, bodyParams, navigate) => {
  if (!API_URL) {
    throw new Error('REACT_APP_API_URL tanımlanmamış. .env dosyasına ekleyin.');
  }

  // --- Duplicate guard (FormData istekleri dosya yükleme olabilir, skip) ---
  const isFormData = bodyParams instanceof FormData;
  const dedupeKey = isFormData ? null : String(process);

  if (dedupeKey && activeRequests.has(dedupeKey)) {
    if (!IS_PROD) {
      console.warn(`%c[fetchWrapper] Duplicate engellendi: process=${dedupeKey}`, 'color:#f59e0b');
    }
    return activeRequests.get(dedupeKey);
  }

  const execute = async () => {
    await acquireSlot();

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    const callingFunction = IS_PROD ? '' : getCallingFunction();
    const requestId = Date.now() + Math.random();

    let requestBody = {};
    if (isFormData) {
      for (let [key, value] of bodyParams.entries()) {
        if (key === 'EVEFILE[]') continue;
        requestBody[key] = value;
      }
    } else {
      requestBody = bodyParams;
    }

    let options = {
      method: 'POST',
      signal: controller.signal,
    };

    if (isFormData) {
      bodyParams.append('process', process);
      options.body = bodyParams;
    } else {
      options.headers = { 'Content-Type': 'application/json' };
      options.body = JSON.stringify({ ...bodyParams, process });
    }

    try {
      const startTime = performance.now();

      const response = await fetch(API_URL, options);
      const responseText = await response.text();

      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        if (responseText.includes('<b>Fatal error</b>') || responseText.includes('<b>Parse error</b>')) {
          const errorMatch = responseText.match(/<b>(.*?)<\/b>/g);
          const errorText = errorMatch ? errorMatch.map(m => m.replace(/<\/?b>/g, '')).join(' ') : 'PHP Error';
          console.error('Backend PHP Error:', errorText);
          throw new Error(`Backend PHP Error: ${errorText}`);
        }

        if (responseText.includes('<b>Warning</b>') || responseText.includes('<br />')) {
          if (!IS_PROD) console.warn('⚠️ PHP Warning tespit edildi, JSON temizleniyor...');
          const jsonStart = responseText.indexOf('{');
          if (jsonStart !== -1) {
            try {
              responseData = JSON.parse(responseText.substring(jsonStart));
            } catch (retryError) {
              const snippet = IS_PROD ? '' : ` Response: ${responseText.substring(0, 200)}`;
              throw new Error(`JSON parse hatası: ${retryError.message}.${snippet}`);
            }
          } else {
            const snippet = IS_PROD ? '' : ` Response: ${responseText.substring(0, 200)}`;
            throw new Error(`JSON parse hatası: JSON başlangıcı bulunamadı.${snippet}`);
          }
        } else {
          const snippet = IS_PROD ? '' : ` Response: ${responseText.substring(0, 200)}`;
          if (!IS_PROD && responseText.trimStart().startsWith('<')) {
            throw new Error(`API JSON yerine HTML döndürdü. .env dosyasındaki REACT_APP_API_URL ve process ID değerlerini kontrol edin.${snippet}`);
          }
          throw new Error(`JSON parse hatası: ${parseError.message}.${snippet}`);
        }
      }

      const endTime = performance.now();
      const duration = endTime - startTime;

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }

      try {
        if (!IS_PROD && window.debugContext && typeof window.debugContext.addDebugLog === 'function') {
          window.debugContext.addDebugLog({
            type: 'REQUEST',
            processId: process,
            callingFunction,
            requestBody: redactSensitive(requestBody),
            responseData: redactSensitive(responseData),
            duration: `${duration.toFixed(2)}ms`,
            hasDebugDiagrun: !!responseData.debug_diagrun,
            debugDiagrun: responseData.debug_diagrun || null,
            requestId
          });
        }
      } catch {
        // Silent fail
      }

      if (responseData.token_return === "false") {
        localStorage.removeItem('token');
        navigate('/login');
      }

      return responseData;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error(`İstek zaman aşımına uğradı (${REQUEST_TIMEOUT / 1000}s): process=${process}`);
      }

      try {
        if (!IS_PROD && window.debugContext && typeof window.debugContext.addDebugLog === 'function') {
          window.debugContext.addDebugLog({
            type: 'ERROR',
            processId: process,
            callingFunction,
            requestBody: redactSensitive(requestBody),
            error: error.message,
            requestId
          });
        }
      } catch {
        // Silent fail
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
      releaseSlot();
    }
  };

  const promise = execute();

  if (dedupeKey) {
    activeRequests.set(dedupeKey, promise);
    promise.finally(() => activeRequests.delete(dedupeKey));
  }

  return promise;
};
